import { ReactNode, useCallback, useMemo, createContext, useContext, useEffect } from 'react'

import { setCurrentBugsnagUser } from '@legacy/initializers/bugsnagHelpers'
import WhoAmI from '@legacy/models/WhoAmI'
import * as routePaths from '@legacy/routePaths'
import { signInPath, resetPasswordPath, acceptInvitationPath, setPasswordPath } from '@legacy/routePaths'
import { useLocation, useNavigate, useRouteLoaderData } from 'react-router'

import { refreshTokenIfExpiring } from '@shared/utils/authHelpers'
import { authLoader } from '@shared/utils/authLoader'
import { AUTH_COOKIE_NAME, CookieManager, LAST_USER_ACTIVE_TIME_COOKIE_NAME } from '@shared/utils/CookieManager'

export type DecodedJwt = {
  iat: number
  exp: number
  gid: string
}

export type ContextType = {
  getToken: (refreshLastUserActiveTime?: boolean) => string | null | undefined
  whoAmI: WhoAmI | null | undefined
  resetAuthentication: () => void
  onAuthenticated: (whoAmI: WhoAmI, token: string | null | undefined) => void
  disabled?: boolean
}

const AuthenticationContext = createContext<ContextType | undefined>(undefined)

const useAuthentication = () => {
  const context = useContext(AuthenticationContext)
  if (!context) {
    throw new Error(`useAuthentication must be used within an AuthenticationProvider`)
  }
  return context
}

interface ProviderProps {
  children: ReactNode
  disable?: boolean
}

const AuthenticationProvider = ({ children, disable }: ProviderProps) => {
  const { whoAmI } = useRouteLoaderData<typeof authLoader>('protected-layout') ?? {}
  const navigate = useNavigate()
  const cookieManager = useMemo(() => new CookieManager(AUTH_COOKIE_NAME), [])
  const lastUserActiveTimeCookieManager = useMemo(() => new CookieManager(LAST_USER_ACTIVE_TIME_COOKIE_NAME), [])
  const location = useLocation()

  const onAuthenticated = (whoAmI: WhoAmI, token: string | null | undefined) => {
    setCurrentBugsnagUser(whoAmI)
    if (token) cookieManager.setCookieJwt(token)
  }

  const getToken = (refreshLastUserActiveTime = true) => {
    const cookie = cookieManager.getCookieJwt()

    if (refreshLastUserActiveTime) {
      lastUserActiveTimeCookieManager.setCookie(Date.now())
    }

    if (cookie.status === 'success') return cookie.value

    return null
  }

  const resetAuthentication = useCallback(() => {
    setCurrentBugsnagUser(undefined)
    cookieManager.deleteCookie()
  }, [cookieManager])

  const value: ContextType = {
    getToken,
    whoAmI: whoAmI as unknown as WhoAmI,
    resetAuthentication,
    onAuthenticated,
    disabled: disable,
  }

  const isUnauthenticatedPath = [signInPath(), resetPasswordPath(), acceptInvitationPath(), setPasswordPath()].includes(
    location.pathname,
  )

  /**
   * The lastUserActiveTimeCookieManager stores the last time the getToken function was called. This function is only called by
   * API functions so this lets us determine that the user is actively using the application. The caveat here is that any
   * page with polling will always appear as if the user is active.
   *
   * If the cookie and the lastUserActiveTimeCookieManager are both greater than the expiry threshold, then this
   * useEffect will refresh the token.
   *
   * If the cookie expired, then automatically navigate the user to the login screen.
   */
  useEffect(() => {
    if (disable || isUnauthenticatedPath) return
    const interval = setInterval(async () => {
      const cookie = cookieManager.getCookieJwt()

      if (cookie.status !== 'success' || !cookie.expiresAt || Date.now() > cookie?.expiresAt) {
        cookieManager.deleteCookie()
        return navigate(routePaths.signInPath())
      }

      await refreshTokenIfExpiring()
    }, 1000)

    return () => clearInterval(interval)
  }, [isUnauthenticatedPath, cookieManager, disable, navigate, lastUserActiveTimeCookieManager])

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>
}

export { AuthenticationProvider, useAuthentication }
